import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Link from '@tiptap/extension-link';
import ResizableImage from './ResizableImageExtension';
import { apiRequest } from '../../config/api';
import {
  Bold, Italic, Strikethrough, Code, List, ListOrdered,
  CheckSquare, Heading1, Heading2, Heading3, Quote, Minus,
  Link as LinkIcon, Undo2, Redo2, Type, Braces,
  ImageIcon, Loader2,
} from 'lucide-react';

/* ─── Image Upload Helper ─────────────────────────── */
async function uploadImage(file) {
  const extension = file.name.split('.').pop().toLowerCase();

  // Step 1: Get signed upload URL
  const urlRes = await apiRequest('/media/upload-url', {
    method: 'POST',
    body: JSON.stringify({
      file_extension: extension,
      file_type: file.type,
      file_size: file.size,
    }),
  });

  if (!urlRes.ok) throw new Error('Failed to get upload URL');
  const { upload_url, file_path, public_url } = await urlRes.json();

  // Step 2: Upload to Supabase
  const uploadRes = await fetch(upload_url, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type },
  });

  if (!uploadRes.ok) throw new Error('Failed to upload file');

  // Step 3: Create media record
  await apiRequest('/media', {
    method: 'POST',
    body: JSON.stringify({
      filename: file.name,
      original_filename: file.name,
      storage_path: file_path,
      public_url,
      file_type: file.type,
      file_size: file.size,
    }),
  });

  return public_url;
}

/* ─── Toolbar Button ──────────────────────────────── */
function ToolbarButton({ onClick, isActive, children, title, disabled, className = '' }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onMouseDown={(e) => {
        e.preventDefault();
        if (!disabled) onClick();
      }}
      className={`p-1 rounded transition-colors ${
        disabled
          ? 'text-gray-200 cursor-not-allowed'
          : isActive
            ? 'bg-gray-200 text-gray-900'
            : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
      } ${className}`}
      title={title}
    >
      {children}
    </button>
  );
}

/* ─── Fixed Toolbar ───────────────────────────────── */
function FixedToolbar({ editor, onImageUpload, imageUploading }) {
  if (!editor) return null;
  const ic = "w-3.5 h-3.5";

  const setLink = () => {
    const prev = editor.getAttributes('link').href;
    const url = window.prompt('URL', prev);
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  return (
    <div className="flex items-center gap-0.5 px-3 py-1.5 border-b border-gray-100">
      <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} isActive={editor.isActive('heading', { level: 1 })} title="Heading 1">
        <Heading1 className={ic} />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} isActive={editor.isActive('heading', { level: 2 })} title="Heading 2">
        <Heading2 className={ic} />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} isActive={editor.isActive('heading', { level: 3 })} title="Heading 3">
        <Heading3 className={ic} />
      </ToolbarButton>

      <div className="w-px h-4 bg-gray-100 mx-1" />

      <ToolbarButton onClick={() => editor.chain().focus().toggleBold().run()} isActive={editor.isActive('bold')} title="Bold ⌘B">
        <Bold className={ic} />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleItalic().run()} isActive={editor.isActive('italic')} title="Italic ⌘I">
        <Italic className={ic} />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleStrike().run()} isActive={editor.isActive('strike')} title="Strikethrough ⌘⇧X">
        <Strikethrough className={ic} />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleCode().run()} isActive={editor.isActive('code')} title="Code ⌘E">
        <Code className={ic} />
      </ToolbarButton>

      <div className="w-px h-4 bg-gray-100 mx-1" />

      <ToolbarButton onClick={() => editor.chain().focus().toggleBulletList().run()} isActive={editor.isActive('bulletList')} title="Bullet list">
        <List className={ic} />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleOrderedList().run()} isActive={editor.isActive('orderedList')} title="Ordered list">
        <ListOrdered className={ic} />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleTaskList().run()} isActive={editor.isActive('taskList')} title="Task list">
        <CheckSquare className={ic} />
      </ToolbarButton>

      <div className="w-px h-4 bg-gray-100 mx-1" />

      <ToolbarButton onClick={() => editor.chain().focus().toggleBlockquote().run()} isActive={editor.isActive('blockquote')} title="Quote">
        <Quote className={ic} />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleCodeBlock().run()} isActive={editor.isActive('codeBlock')} title="Code block">
        <Braces className={ic} />
      </ToolbarButton>
      <ToolbarButton onClick={setLink} isActive={editor.isActive('link')} title="Link ⌘K">
        <LinkIcon className={ic} />
      </ToolbarButton>
      <ToolbarButton onClick={onImageUpload} disabled={imageUploading} title="Image">
        {imageUploading ? <Loader2 className={`${ic} animate-spin`} /> : <ImageIcon className={ic} />}
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Divider">
        <Minus className={ic} />
      </ToolbarButton>

      <div className="flex-1" />

      <ToolbarButton onClick={() => editor.chain().focus().undo().run()} title="Undo ⌘Z">
        <Undo2 className={ic} />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().redo().run()} title="Redo ⌘⇧Z">
        <Redo2 className={ic} />
      </ToolbarButton>
    </div>
  );
}

/* ─── Floating Toolbar (on text selection) ────────── */
function FloatingToolbar({ editor }) {
  const [coords, setCoords] = useState(null);

  const updatePosition = useCallback(() => {
    if (!editor) return;
    const { from, to, empty } = editor.state.selection;
    if (empty || from === to) {
      setCoords(null);
      return;
    }
    const { view } = editor;
    const start = view.coordsAtPos(from);
    const end = view.coordsAtPos(to);
    const editorEl = view.dom.closest('.rich-text-editor');
    if (!editorEl) return;
    const rect = editorEl.getBoundingClientRect();

    setCoords({
      top: start.top - rect.top - 44,
      left: (start.left + end.right) / 2 - rect.left,
    });
  }, [editor]);

  useEffect(() => {
    if (!editor) return;
    editor.on('selectionUpdate', updatePosition);
    const handleBlur = () => setCoords(null);
    editor.on('blur', handleBlur);
    return () => {
      editor.off('selectionUpdate', updatePosition);
      editor.off('blur', handleBlur);
    };
  }, [editor, updatePosition]);

  if (!coords || !editor) return null;

  const ic = "w-3.5 h-3.5";
  const btnClass = "!text-gray-400 hover:!text-white hover:!bg-gray-700";

  const setLink = () => {
    const prev = editor.getAttributes('link').href;
    const url = window.prompt('URL', prev);
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  return (
    <div
      className="rich-text-floating-toolbar absolute z-50 flex items-center gap-0.5 px-1.5 py-1 bg-gray-900 rounded-lg shadow-xl"
      style={{ top: `${coords.top}px`, left: `${coords.left}px`, transform: 'translateX(-50%)' }}
    >
      <ToolbarButton onClick={() => editor.chain().focus().toggleBold().run()} isActive={editor.isActive('bold')} title="Bold" className={btnClass}>
        <Bold className={ic} />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleItalic().run()} isActive={editor.isActive('italic')} title="Italic" className={btnClass}>
        <Italic className={ic} />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleStrike().run()} isActive={editor.isActive('strike')} title="Strikethrough" className={btnClass}>
        <Strikethrough className={ic} />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleCode().run()} isActive={editor.isActive('code')} title="Code" className={btnClass}>
        <Code className={ic} />
      </ToolbarButton>
      <div className="w-px h-4 bg-gray-700 mx-0.5" />
      <ToolbarButton onClick={setLink} isActive={editor.isActive('link')} title="Link" className={btnClass}>
        <LinkIcon className={ic} />
      </ToolbarButton>
    </div>
  );
}

/* ─── Slash Command Menu ──────────────────────────── */
const SLASH_COMMANDS = [
  { id: 'h1', label: 'Heading 1', description: 'Large heading', icon: Heading1, action: (e) => e.chain().focus().toggleHeading({ level: 1 }).run() },
  { id: 'h2', label: 'Heading 2', description: 'Medium heading', icon: Heading2, action: (e) => e.chain().focus().toggleHeading({ level: 2 }).run() },
  { id: 'h3', label: 'Heading 3', description: 'Small heading', icon: Heading3, action: (e) => e.chain().focus().toggleHeading({ level: 3 }).run() },
  { id: 'bullet', label: 'Bullet list', description: 'Unordered list', icon: List, action: (e) => e.chain().focus().toggleBulletList().run() },
  { id: 'ordered', label: 'Numbered list', description: 'Ordered list', icon: ListOrdered, action: (e) => e.chain().focus().toggleOrderedList().run() },
  { id: 'task', label: 'Task list', description: 'Checklist items', icon: CheckSquare, action: (e) => e.chain().focus().toggleTaskList().run() },
  { id: 'quote', label: 'Quote', description: 'Block quote', icon: Quote, action: (e) => e.chain().focus().toggleBlockquote().run() },
  { id: 'code', label: 'Code block', description: 'Code snippet', icon: Braces, action: (e) => e.chain().focus().toggleCodeBlock().run() },
  { id: 'divider', label: 'Divider', description: 'Horizontal line', icon: Minus, action: (e) => e.chain().focus().setHorizontalRule().run() },
  { id: 'image', label: 'Image', description: 'Upload an image', icon: ImageIcon, action: null }, // handled specially
  { id: 'text', label: 'Text', description: 'Plain paragraph', icon: Type, action: (e) => e.chain().focus().setParagraph().run() },
];

function SlashCommandMenu({ editor, coords, query, onClose, onImageUpload }) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const menuRef = useRef(null);

  const filtered = SLASH_COMMANDS.filter(
    (cmd) => cmd.label.toLowerCase().includes(query.toLowerCase()) || cmd.description.toLowerCase().includes(query.toLowerCase())
  );

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  const executeCommand = useCallback((cmd) => {
    const { from } = editor.state.selection;
    const slashPos = from - query.length - 1;
    editor.chain().focus().deleteRange({ from: slashPos, to: from }).run();

    if (cmd.id === 'image') {
      onImageUpload();
    } else {
      cmd.action(editor);
    }
    onClose();
  }, [editor, query, onClose, onImageUpload]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((i) => (i + 1) % filtered.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((i) => (i - 1 + filtered.length) % filtered.length);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (filtered[selectedIndex]) executeCommand(filtered[selectedIndex]);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [filtered, selectedIndex, executeCommand, onClose]);

  if (filtered.length === 0) return null;

  return (
    <div
      ref={menuRef}
      className="slash-command-menu absolute z-50 bg-white rounded-xl shadow-xl border border-gray-200 py-1.5 w-56 overflow-hidden"
      style={{ top: `${coords.top}px`, left: `${coords.left}px` }}
    >
      <div className="px-3 py-1.5">
        <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">Blocks</span>
      </div>
      {filtered.map((cmd, i) => {
        const Icon = cmd.icon;
        return (
          <button
            key={cmd.id}
            onMouseDown={(e) => {
              e.preventDefault();
              executeCommand(cmd);
            }}
            onMouseEnter={() => setSelectedIndex(i)}
            className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${
              i === selectedIndex ? 'bg-gray-50' : ''
            }`}
          >
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
              i === selectedIndex ? 'bg-gray-200 text-gray-700' : 'bg-gray-100 text-gray-400'
            }`}>
              <Icon className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium text-gray-900">{cmd.label}</div>
              <div className="text-xs text-gray-400">{cmd.description}</div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

/* ─── Main Editor ─────────────────────────────────── */
export default function RichTextEditor({ content, onChange, placeholder = 'Type / for commands...' }) {
  const [slashMenu, setSlashMenu] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleImageFiles = useCallback(async (files, editorInstance) => {
    if (!editorInstance) return;
    const imageFiles = Array.from(files).filter(f => f.type.startsWith('image/'));
    if (imageFiles.length === 0) return;

    setImageUploading(true);
    try {
      for (const file of imageFiles) {
        // Insert a local preview immediately so user sees feedback
        const localUrl = URL.createObjectURL(file);
        const placeholderId = `uploading-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        editorInstance.chain().focus().setImage({ src: localUrl, alt: placeholderId, title: 'Uploading...' }).run();

        try {
          const url = await uploadImage(file);
          // Find and replace the placeholder image with the real URL
          const { doc } = editorInstance.state;
          let placeholderPos = null;
          doc.descendants((node, pos) => {
            if (node.type.name === 'image' && node.attrs.alt === placeholderId) {
              placeholderPos = pos;
              return false;
            }
          });
          if (placeholderPos !== null) {
            const { tr } = editorInstance.state;
            tr.setNodeMarkup(placeholderPos, undefined, {
              src: url,
              alt: file.name,
              title: null,
            });
            editorInstance.view.dispatch(tr);
          }
        } catch (err) {
          // Remove the placeholder on failure
          const { doc } = editorInstance.state;
          doc.descendants((node, pos) => {
            if (node.type.name === 'image' && node.attrs.alt === placeholderId) {
              const { tr } = editorInstance.state;
              tr.delete(pos, pos + node.nodeSize);
              editorInstance.view.dispatch(tr);
              return false;
            }
          });
          console.error('Image upload failed:', err);
        } finally {
          URL.revokeObjectURL(localUrl);
        }
      }
    } finally {
      setImageUploading(false);
    }
  }, []);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Placeholder.configure({
        placeholder,
        emptyEditorClass: 'is-editor-empty',
      }),
      TaskList,
      TaskItem.configure({ nested: true }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { class: 'text-brand-600 underline underline-offset-2 hover:text-brand-700 cursor-pointer' },
      }),
      ResizableImage.configure({
        HTMLAttributes: { class: 'rich-text-image' },
        allowBase64: false,
      }),
    ],
    content: content || '',
    editorProps: {
      attributes: {
        class: 'focus:outline-none min-h-[200px] px-6 py-4',
      },
      // Handle pasted images
      handlePaste: (view, event) => {
        const items = event.clipboardData?.items;
        if (!items) return false;
        const imageFiles = [];
        for (const item of items) {
          if (item.type.startsWith('image/')) {
            const file = item.getAsFile();
            if (file) imageFiles.push(file);
          }
        }
        if (imageFiles.length > 0) {
          handleImageFiles(imageFiles, view.state.plugins[0]?.spec?.editor || null);
          return false; // let the editor handle it after we upload
        }
        return false;
      },
      // Handle dropped images
      handleDrop: (view, event) => {
        const files = event.dataTransfer?.files;
        if (!files || files.length === 0) return false;
        const imageFiles = Array.from(files).filter(f => f.type.startsWith('image/'));
        if (imageFiles.length === 0) return false;
        event.preventDefault();
        // We'll use the editor ref from closure
        return true; // handled
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange(html);

      // Detect slash command
      const { from } = editor.state.selection;
      const $pos = editor.state.doc.resolve(from);
      const textBefore = $pos.parent.textContent.slice(0, $pos.parentOffset);
      const slashMatch = textBefore.match(/\/(\w*)$/);

      if (slashMatch) {
        const { view } = editor;
        const coords = view.coordsAtPos(from);
        const editorEl = view.dom.closest('.rich-text-editor');
        if (editorEl) {
          const rect = editorEl.getBoundingClientRect();
          setSlashMenu({
            query: slashMatch[1],
            coords: {
              top: coords.bottom - rect.top + 4,
              left: coords.left - rect.left,
            },
          });
        }
      } else {
        setSlashMenu(null);
      }
    },
  });

  // Handle paste/drop with editor reference
  useEffect(() => {
    if (!editor) return;
    const editorEl = editor.view.dom;

    const handlePaste = (event) => {
      const items = event.clipboardData?.items;
      if (!items) return;
      const imageFiles = [];
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) imageFiles.push(file);
        }
      }
      if (imageFiles.length > 0) {
        event.preventDefault();
        handleImageFiles(imageFiles, editor);
      }
    };

    const handleDrop = (event) => {
      const files = event.dataTransfer?.files;
      if (!files || files.length === 0) return;
      const imageFiles = Array.from(files).filter(f => f.type.startsWith('image/'));
      if (imageFiles.length === 0) return;
      event.preventDefault();
      handleImageFiles(imageFiles, editor);
    };

    editorEl.addEventListener('paste', handlePaste);
    editorEl.addEventListener('drop', handleDrop);
    return () => {
      editorEl.removeEventListener('paste', handlePaste);
      editorEl.removeEventListener('drop', handleDrop);
    };
  }, [editor, handleImageFiles]);

  // Sync external content changes
  useEffect(() => {
    if (editor && content !== undefined) {
      const currentHTML = editor.getHTML();
      if (content !== currentHTML && !(content === '' && currentHTML === '<p></p>')) {
        editor.commands.setContent(content || '', false);
      }
    }
  }, [content, editor]);

  // Close slash menu when editor loses focus
  useEffect(() => {
    if (!editor) return;
    const handleBlur = () => setSlashMenu(null);
    editor.on('blur', handleBlur);
    return () => editor.off('blur', handleBlur);
  }, [editor]);

  const triggerImageUpload = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileInputChange = useCallback((e) => {
    const files = e.target.files;
    if (files && files.length > 0 && editor) {
      handleImageFiles(files, editor);
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  }, [editor, handleImageFiles]);

  return (
    <div className="rich-text-editor relative">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
        onChange={handleFileInputChange}
        className="hidden"
        multiple
      />
      <FixedToolbar editor={editor} onImageUpload={triggerImageUpload} imageUploading={imageUploading} />
      <FloatingToolbar editor={editor} />
      <EditorContent editor={editor} />
      {slashMenu && editor && (
        <SlashCommandMenu
          editor={editor}
          coords={slashMenu.coords}
          query={slashMenu.query}
          onClose={() => setSlashMenu(null)}
          onImageUpload={triggerImageUpload}
        />
      )}
    </div>
  );
}
